import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import MapView from "../components/Map/MapView";
import API_BASE from "../config";
import { STEPS } from "../workflow/steps";
import proj4 from "proj4";
import { getUtmProj } from "../drawing/utils/projection";
import { getMidfieldLine } from "../drawing/utils/geometry";
import { mastRechteckAus, layoutAusSymmetrie } from "../drawing/utils/layoutErkennung";

// ── Zwei Hilfslinien, die nie gespeichert werden ─────────────────────
// Mittellinie und Mast-Rechteck sind reine Zeichenhilfen und stehen in
// keiner Tabelle. Beim Bearbeiten fehlten sie deshalb auf der Karte
// (gemeldet 10.08.). Beide lassen sich exakt zurueckrechnen:

// Mittellinie: dieselbe Funktion wie beim Zeichnen, und wie dort in UTM
// gerechnet — in reinen Grad wuerde bei Nord-Sued-Feldern die falsche
// Kante als "lang" gelten (die Warnung steht bei getMidfieldLine).
const mittellinieAus = (fieldRectangle) => {
  if (!fieldRectangle || fieldRectangle.length < 4) return null;
  const wgs84 = "EPSG:4326";
  const utm = getUtmProj(fieldRectangle[0]);
  const mid = getMidfieldLine(fieldRectangle.map((p) => proj4(wgs84, utm, p)));
  return mid ? mid.map((p) => proj4(utm, wgs84, p)) : null;
};

// mastRechteckAus + layoutAusSymmetrie leben seit 21.09.2026 in
// drawing/utils/layoutErkennung.js (Preset-Erkennung mit geometrischem
// Tie-Break fuer die seit 26.08. mehrdeutigen symmetry-Arrays).

// Projekte ohne Preset (Sandbox: 201 von 351, symmetry = {1,2,…,N} =
// "N unabhaengige Masten") bekommen ein Ersatz-Layout aus ihren eigenen
// gespeicherten Werten. Damit laesst sich auch dort bearbeiten und neu
// rechnen; der Submit schickt genau die Werte zurueck, die schon in der
// Datenbank stehen, die Semantik bleibt also unangetastet.
const ersatzLayout = (angles, symmetry, raster) => ({
  id: "gespeichert",
  name: `Gespeichertes Layout (${symmetry.length} Lichtpunkte)`,
  description: "aus dem Projekt uebernommen",
  img: null,
  edges: raster ? raster.map((r) => r.edge) : symmetry.map(() => 0),
  t: raster ? raster.map((r) => r.t) : symmetry.map(() => 0),
  angles,
  symmetry,
});

// Gespeicherte Ausrichtungspfeile auf Zeichenlaenge bringen. In der
// Datenbank steht die volle Ziellinie (Mast bis Zielpunkt, oft quer ueber
// das ganze Feld); der Zeichner stellt Pfeile immer 15 m lang dar. Ohne das
// liegt nach dem Laden ein Liniengewirr auf der Karte. Nur die Anzeige —
// die Richtung bleibt exakt, und genau die misst restoreFromSketch aus.
const PFEIL_LAENGE_M = 15;
const pfeileKuerzen = (mastPoints, mastArrows) => {
  if (!mastPoints?.length || !mastArrows?.length) return mastArrows || [];
  const wgs84 = "EPSG:4326";
  const utm = getUtmProj(mastPoints[0]);
  return mastArrows.map((arrow, i) => {
    if (!arrow || arrow.length < 2) return arrow;
    const start = proj4(wgs84, utm, arrow[0]);
    const ende = proj4(wgs84, utm, arrow[1]);
    const dx = ende[0] - start[0];
    const dy = ende[1] - start[1];
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return arrow;
    return [
      arrow[0],
      proj4(utm, wgs84, [
        start[0] + (dx / len) * PFEIL_LAENGE_M,
        start[1] + (dy / len) * PFEIL_LAENGE_M,
      ]),
    ];
  });
};

// Kantenraster geometrisch bestimmen — fuer Projekte ohne Preset.
// Jeder Mast wird der Feldseite zugeordnet, auf der er liegt, und bekommt
// seinen Parameter t entlang der Feld-Laengsachse. Gerechnet in UTM.
//
// Kante 2 wird BEWUSST rueckwaerts parametrisiert (t = 1 − s): so wie in
// mastlayouts.js, wo der Kantenvektor auf der Gegenseite rueckwaerts
// laeuft. Ohne das entstuende beim Zurueckrechnen ein ueberkreuztes
// Viereck statt eines Rechtecks.
const rasterAusGeometrie = (fieldRectangle, mastPoints) => {
  if (!fieldRectangle || fieldRectangle.length < 4 || !mastPoints?.length) return null;
  const wgs84 = "EPSG:4326";
  const utm = getUtmProj(fieldRectangle[0]);
  const ecken = fieldRectangle.slice(0, 4).map((p) => proj4(wgs84, utm, p));

  // Laengsachse des Feldes finden (laengere der beiden Kantenrichtungen).
  const kante = (i, j) => [ecken[j][0] - ecken[i][0], ecken[j][1] - ecken[i][1]];
  const a = kante(0, 1);
  const b = kante(1, 2);
  const achse = Math.hypot(a[0], a[1]) >= Math.hypot(b[0], b[1]) ? a : b;
  const len = Math.hypot(achse[0], achse[1]);
  if (len < 1e-6) return null;
  const u = [achse[0] / len, achse[1] / len];      // entlang
  const n = [-u[1], u[0]];                          // quer
  const mitte = ecken.reduce(
    (acc, p) => [acc[0] + p[0] / 4, acc[1] + p[1] / 4], [0, 0]
  );

  const werte = mastPoints.map((p) => {
    const P = proj4(wgs84, utm, p);
    const d = [P[0] - mitte[0], P[1] - mitte[1]];
    return { s: d[0] * u[0] + d[1] * u[1], seite: d[0] * n[0] + d[1] * n[1] >= 0 ? 2 : 0 };
  });

  // t je Seite auf [0,1] normieren — die aeussersten Masten definieren die Ecken.
  const raster = werte.map(() => null);
  [0, 2].forEach((seite) => {
    const idx = werte.map((w, i) => (w.seite === seite ? i : -1)).filter((i) => i >= 0);
    if (!idx.length) return;
    const min = Math.min(...idx.map((i) => werte[i].s));
    const max = Math.max(...idx.map((i) => werte[i].s));
    const spanne = max - min;
    idx.forEach((i) => {
      const anteil = spanne > 1e-6 ? (werte[i].s - min) / spanne : 0;
      raster[i] = { edge: seite, t: seite === 2 ? 1 - anteil : anteil, angle: 0 };
    });
  });
  return raster.every(Boolean) ? raster : null;
};

// ── SketchPage ─────────────────────────────────────────────────────────
// Step 2 of the new two-phase create flow. The user already created the
// project + picked the address on CreateProjectPage; this page wraps the
// existing Sidebar wizard + MapView to actually DRAW the field + masts
// + buildings, then POSTs the geometry to /projects/:id/sketch.
//
// Loads the project on mount to pre-populate projectDetails (address,
// town, name, number) so the wizard skips the address step and starts
// at DRAW_FIELD_RECTANGLE. After the calculation finishes, navigates to
// /projects/:id so the user sees the comparison table + sweep variants.
// ───────────────────────────────────────────────────────────────────────
const SketchPage = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // CreateProjectPage stashes the picked address pin + zoom in router
  // state when navigating here, so the wizard map opens centred on the
  // sportplatz instead of the hard-coded default. Falls back to the
  // Nominatim lookup below if the user reloaded the page (state lost)
  // or arrived via /projects/:id → "Neu skizzieren".
  const initialMapView = location.state?.initialMapView || null;
  // „Skizze bearbeiten" auf der Projektseite: dann holen wir die
  // gespeicherte Zeichnung zurück, statt mit leerer Karte zu starten,
  // und springen an den Schritt vor „Kalkulation starten". Ohne dieses
  // Flag (Knopf „Neu skizzieren") bleibt es beim Zeichnen von vorn.
  const resumeSketch = Boolean(location.state?.resumeSketch);

  // Wizard state, scoped to this route. Identical shape to the legacy
  // App.js state — Sidebar + MapView haven't changed, we just own the
  // hooks here instead of at the top-level.
  const [workflowStep, setWorkflowStep] = useState(STEPS.INIT);
  const [selectedLayout, setSelectedLayout] = useState(null);
  // Ersatz-Layout für Projekte ohne Preset (siehe ersatzLayout oben). Steht
  // es, arbeitet die Sidebar beim Submit damit statt mit der Preset-Liste.
  const [layoutOverride, setLayoutOverride] = useState(null);
  // Kantenraster der geladenen Skizze: je Lichtpunkt {edge, t, angle}.
  // null = kein verlässliches Raster → das Mastwerkzeug lässt dann frei
  // ziehen statt auf einer womöglich falschen Kante zu führen.
  const [layoutRaster, setLayoutRaster] = useState(null);
  const [geometries, setGeometries] = useState({
    fieldRectangle: null,
    mastsRectangle: null,
    fieldCenterLine: null,
    mastArrows: [],
    mastPoints: [],
    buildingPolylines: [],
    // Index-parallel zu buildingPolylines (Fassade i <-> Hoehe i), gepflegt
    // von der Fassaden-Tabelle in Schritt 5. Vorgabe 8 m je Fassade.
    buildingHeights: [],
  });
  const [firstABVector, setFirstABVector] = useState(null);
  const [firstUTM, setFirstUTM] = useState(null);
  const [projectDetails, setProjectDetails] = useState({
    projectName: "",
    projectNumber: "",
    mastHeight: "",
    projectAddress: "",
    projectTown: "",
    autoLuminaireSweep: true,
    // Norm-Vorgaben (2026-08-13): null = Norm-Default (75 lx / 0,5 / 55).
    // Editierte Werte wandern über projectDetails in projects_calc_settings.
    emTarget: null,
    uoTarget: null,
    rgMax: null,
  });
  const [calculationStatus, setCalculationStatus] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Resolved map view, set in the load effect: either from Router state
  // (came from CreateProjectPage) or from a Nominatim lookup on the
  // project's stored address (came from ProjectDetailPage → "Neu
  // skizzieren"). MapView reads this when it mounts, so we hold off
  // rendering MapView until we have a value (or have given up).
  const [resolvedView, setResolvedView] = useState(initialMapView);
  // Beim Bearbeiten: Ausschnitt, in den die GANZE Skizze passt (Feld plus
  // Masten plus Fassaden). Ein fester Zoom reicht nicht — die Masten stehen
  // außerhalb des Feldes, und Feldgrößen unterscheiden sich.
  const [resolvedBounds, setResolvedBounds] = useState(null);
  // Hinweiszeile oben in der Sidebar: „Skizze geladen …" bzw. die
  // Fehlermeldung, falls das Zurückholen nicht geklappt hat.
  const [notice, setNotice] = useState(null);
  const mapRef = useRef(null);

  // ── Load existing project once so the wizard skips the address step ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/projects/${projectId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const p = await res.json();
        if (cancelled) return;
        setProjectDetails((prev) => ({
          ...prev,
          projectName: p.project_name || "",
          projectNumber: p.project_number || "",
          projectAddress: p.project_address || "",
          projectTown: p.project_town || "",
          autoLuminaireSweep:
            p.auto_luminaire_sweep === undefined ? true : !!p.auto_luminaire_sweep,
        }));
        // Skip the address step since CreateProjectPage already collected it.
        // Start the user at DRAW_FIELD_RECTANGLE; the address pill at the
        // top of Sidebar still shows so they can see what they picked.
        setWorkflowStep(STEPS.DRAW_FIELD_RECTANGLE);

        // ── „Bearbeiten": gespeicherte Skizze zurückholen ────────────
        // GET /projects/:id/sketch liefert Feld, Masten, Pfeile und
        // Fassaden in genau dem Format, in dem der Zeichner sie hält.
        // Wir hydrieren damit den Wizard-State (die Karte rendert
        // ausschließlich aus diesem State) und setzen den Schritt auf
        // SUBMIT_PROJECT — also genau vor „Kalkulation starten". Jeder
        // vorige Schritt ist von dort per ✓-Button erreichbar.
        // Klappt etwas nicht (Layout nicht erkennbar, alte Projekte ohne
        // Masten, Netzwerkfehler), bleibt der normale Neu-Zeichnen-Flow
        // stehen und der Nutzer bekommt den Grund als Hinweis zu sehen.
        let resumed = false;
        if (resumeSketch) {
          try {
            const sRes = await fetch(`${API_BASE}/projects/${projectId}/sketch`);
            if (!sRes.ok) {
              const detail = await sRes.json().catch(() => ({}));
              throw new Error(detail.detail || `HTTP ${sRes.status}`);
            }
            const sketch = await sRes.json();
            const g = sketch.geometries || {};
            const angles = sketch.mastLayout?.angles || [];
            const symmetry = sketch.mastLayout?.symmetry || [];
            const punkte = g.mastPoints || [];

            // Sicherheitsnetz: zu jedem gespeicherten symmetry-Wert muss es
            // einen Mastpunkt geben, sonst liefe der Submit die Werte gegen
            // die falschen Masten.
            if (!punkte.length || punkte.length !== symmetry.length) {
              throw new Error(
                `Anzahl Masten (${punkte.length}) passt nicht zu den gespeicherten Werten (${symmetry.length})`
              );
            }

            // Preset über symmetry suchen (bei mehrdeutiger symmetry
            // entscheiden Mastgeometrie und gespeicherte Pfeile, siehe
            // layoutErkennung.js);
            // sonst Ersatz-Layout aus den gespeicherten Werten, damit auch
            // Projekte ohne Preset bearbeitbar sind (Sandbox: 201 von 351).
            const preset = layoutAusSymmetrie(
              symmetry, punkte, g.mastArrows || [], g.fieldRectangle || null
            );
            const gemessenesRaster = preset
              ? null
              : rasterAusGeometrie(g.fieldRectangle, punkte);
            const layout = preset || ersatzLayout(angles, symmetry, gemessenesRaster);

            // Raster nur weitergeben, wenn es echt ist: Preset-Werte oder
            // aus der Geometrie gemessen. Beim Ersatz-Layout ohne Messung
            // stehen dort Nullen — damit würde die Schiene alle Masten auf
            // Kante 0 zwingen, also lieber frei ziehen lassen.
            // preset:true sagt dem Mastwerkzeug, dass die Winkel aus dem
            // Katalog stammen — dann rechnet es die Ausrichtung nach der
            // heutigen Regel neu, statt die gespeicherte zu messen.
            const raster = (preset || gemessenesRaster)
              ? layout.edges.map((edge, i) => ({
                  edge, t: layout.t[i], angle: layout.angles[i],
                  preset: Boolean(preset),
                }))
              : null;

            if (cancelled) return;
            setSelectedLayout(layout.id);
            setLayoutOverride(preset ? null : layout);
            setLayoutRaster(raster);
            setGeometries({
              fieldRectangle: g.fieldRectangle || null,
              // Beide Hilfslinien werden nicht gespeichert und deshalb hier
              // zurueckgerechnet (siehe oben) — sonst fehlen sie beim
              // Bearbeiten auf der Karte.
              mastsRectangle: mastRechteckAus(layout, punkte),
              fieldCenterLine: mittellinieAus(g.fieldRectangle),
              mastArrows: pfeileKuerzen(punkte, g.mastArrows || []),
              mastPoints: punkte,
              buildingPolylines: g.buildingPolylines || [],
              // Gespeicherte Gebaeudehoehen (Building.height) zurueckholen,
              // damit "Bearbeiten" nicht stillschweigend auf 8 m zurueckfaellt.
              buildingHeights: g.buildingHeights || [],
            });
            setProjectDetails((prev) => ({
              ...prev,
              mastHeight: sketch.projectDetails?.mastHeight ?? prev.mastHeight,
              cct: sketch.projectDetails?.cct ?? null,
              autoLuminaireSweep: sketch.projectDetails?.autoLuminaireSweep !== false,
              // Editierte Norm-Vorgaben zurückholen (null = Norm-Default),
              // damit "Bearbeiten" sie nicht stillschweigend zurücksetzt.
              emTarget: sketch.projectDetails?.emTarget ?? null,
              uoTarget: sketch.projectDetails?.uoTarget ?? null,
              rgMax: sketch.projectDetails?.rgMax ?? null,
            }));
            // Karte auf die geladene Skizze setzen statt auf den Adress-Pin.
            // Mittelpunkt als Startwert, Feinjustierung macht MapView per
            // fitBounds über ALLE Punkte der Skizze — Masten stehen außerhalb
            // des Feldes und dürfen nicht aus dem Bild fallen.
            const corners = g.fieldRectangle || [];
            const alle = [
              ...corners,
              ...(g.mastPoints || []),
              ...(g.buildingPolylines || []).flat(),
            ];
            if (corners.length) {
              setResolvedView({
                lon: corners.reduce((sum, c) => sum + c[0], 0) / corners.length,
                lat: corners.reduce((sum, c) => sum + c[1], 0) / corners.length,
                zoom: 17,
              });
            }
            if (alle.length >= 2) {
              const lons = alle.map((p) => p[0]);
              const lats = alle.map((p) => p[1]);
              setResolvedBounds([
                [Math.min(...lons), Math.min(...lats)],
                [Math.max(...lons), Math.max(...lats)],
              ]);
            }
            setWorkflowStep(STEPS.SUBMIT_PROJECT);
            // Veraltetes Preset (Philips Entscheidung 03.09.): Projekte,
            // deren symmetry zu keinem aktuellen Preset passt (v. a. 8-Mast-
            // Projekte vor dem 26.08. mit alter Symmetrie), werden NICHT
            // mehr stillschweigend mit dem Ersatz-Layout gerechnet — das
            // lief ohnehin seit dem 26.08. auf einen stummen HTTP 422
            // (Rainers Abbruch an Prod-217). Stattdessen klare Warnung, der
            // Submit bleibt gesperrt, bis in Schritt 2 ein aktuelles Layout
            // gewählt und das Mastrechteck neu gezogen wurde.
            setNotice(preset
              ? {
                  tone: "info",
                  text: "Gespeicherte Skizze geladen — anpassen und neu rechnen. "
                    + "Die bisherigen Berechnungen bleiben in der Übersicht erhalten."
                    + " Die Mast-Ausrichtung wurde auf die Feldmitte neu bestimmt (Stand 05.08.).",
                }
              : {
                  tone: "warn",
                  text: "Veraltetes Preset: Das gespeicherte Mast-Layout entspricht "
                    + "keinem aktuellen Standard-Layout und kann nicht mehr gerechnet werden. "
                    + "Bitte in Schritt 2 ein aktuelles Layout wählen und die Masten neu setzen. "
                    + "Die bisherigen Berechnungen bleiben in der Übersicht erhalten.",
                });
            resumed = true;
          } catch (e) {
            console.error("Skizze konnte nicht geladen werden", e);
            if (!cancelled) {
              setNotice({
                tone: "warn",
                text: `Skizze konnte nicht geladen werden (${e.message}) — bitte neu zeichnen.`,
              });
            }
          }
        }

        // Resolve the map view BEFORE setLoaded(true) so MapView mounts
        // with the right center. Three paths, in priority order:
        //   1. Router state (just came from CreateProjectPage) →
        //      initialMapView already populated.
        //   2. Project has persisted coords (migration 0009, projects
        //      created from 2026-05-21 onward) → use them directly,
        //      zero network calls.
        //   3. Legacy project without coords → Nominatim fallback on
        //      project_address / project_town, then backfill the
        //      Project via PATCH so the next visit hits path 2.
        if (resumed) {
          // Path 0 (Bearbeiten): der Kartenausschnitt steht schon auf der
          // geladenen Skizze — Adress-Pin und Nominatim wären hier falsch.
        } else if (initialMapView) {
          // Path 1: nothing to do, useState already has it.
        } else if (p.project_lon != null && p.project_lat != null) {
          // Path 2: persisted coords win.
          setResolvedView({
            lon: p.project_lon,
            lat: p.project_lat,
            zoom: 17,
          });
        } else if (p.project_address || p.project_town) {
          // Path 3: one-shot Nominatim + backfill.
          try {
            const q = [p.project_address, p.project_town].filter(Boolean).join(" ");
            const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`;
            const r = await fetch(url);
            const arr = await r.json();
            if (arr && arr[0]) {
              const lon = parseFloat(arr[0].lon);
              const lat = parseFloat(arr[0].lat);
              setResolvedView({ lon, lat, zoom: 17 });
              // Backfill so next time we hit path 2. Best-effort —
              // failure here doesn't break the sketch flow, the user
              // would just hit Nominatim again on the next visit.
              fetch(`${API_BASE}/projects/${projectId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ project_lon: lon, project_lat: lat }),
              }).catch(() => {});
            }
          } catch (_) { /* ignore — best-effort, falls back to default */ }
        }
        setLoaded(true);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e.message);
          setLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Stub — Sidebar wants this prop. We don't list projects in this route
  // (you can't pick another project mid-sketch), so the stub is fine.
  const refreshProjects = async () => {};

  if (!loaded) {
    return (
      <div style={{ padding: "var(--space-8)" }}>
        <div className="empty-state">Lade Projekt {projectId}…</div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div style={{ padding: "var(--space-8)" }}>
        <div className="banner banner-fail" style={{ marginBottom: "var(--space-4)" }}>
          Projekt {projectId} konnte nicht geladen werden: {loadError}
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/projects")}
        >← Zur Projekt-Liste</button>
      </div>
    );
  }

  return (
    <div className="main-content" style={{ display: "flex", flex: 1, minHeight: 0 }}>
      <div className="sidebar">
        <Sidebar
          workflowStep={workflowStep}
          setWorkflowStep={setWorkflowStep}
          selectedLayout={selectedLayout}
          setSelectedLayout={setSelectedLayout}
          setProjectDetails={setProjectDetails}
          projectDetails={projectDetails}
          geometries={geometries}
          setGeometries={setGeometries}
          mapRef={mapRef}
          calculationStatus={calculationStatus}
          setCalculationStatus={setCalculationStatus}
          refreshProjects={refreshProjects}
          projectId={Number(projectId)}
          // Hinweiszeile („Skizze aus … geladen") — nur im Bearbeiten-Fall gesetzt
          notice={notice}
          // Ersatz-Layout (Projekt ohne Preset): Sidebar muss es in den
          // Submit-Payload stecken, mastLayouts.find() greift dort ins Leere.
          layoutOverride={layoutOverride}
          // …und beim Wechsel auf ein Preset muss die Sidebar das
          // Override löschen können, sonst bleibt die Auswahl wirkungslos.
          setLayoutOverride={setLayoutOverride}
          // Don't auto-navigate — the EN-12193 result panel and toast
          // need a moment to render. We pass the navigator down so the
          // sidebar can show a manual "→ Alle Varianten ansehen" button
          // once the calc finishes, and the user clicks when they're ready.
          onAfterSubmit={null}
          navigateToDetail={(pid) => navigate(`/projects/${pid}`)}
        />
      </div>

      <div className="map-wrapper">
        <MapView
          workflowStep={workflowStep}
          geometries={geometries}
          setGeometries={setGeometries}
          firstABVector={firstABVector}
          setFirstABVector={setFirstABVector}
          firstUTM={firstUTM}
          setFirstUTM={setFirstUTM}
          setWorkflowStep={setWorkflowStep}
          selectedLayout={selectedLayout}
          activeProject={null}
          initialMapView={resolvedView}
          initialBounds={resolvedBounds}
          // Kantenraster der geladenen Skizze (Preset oder rekonstruiert) —
          // MapView reicht es an das Mastwerkzeug weiter, damit die Schiene
          // weiß, auf welcher Kante ein Mast sitzt.
          initialRaster={layoutRaster}
          ref={mapRef}
        />
      </div>
    </div>
  );
};

export default SketchPage;
